import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  doc, 
  deleteDoc,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { reviewModel, reviewUtils } from '../data/reviewModel';
import { notificationModel } from '../data/notificationModel';

const ReviewContext = createContext();

export const useReview = () => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return context;
};

export const ReviewProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { createNotification } = useNotification();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load reviews where user is reviewer or reviewee
  const loadUserReviews = () => {
    if (!currentUser) return () => {};

    setLoading(true);
    setError(null);

    const reviewsRef = collection(db, reviewModel.collection);
    const q = query(
      reviewsRef,
      where('reviewerId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const reviewsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually after fetching
        reviewsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setReviews(reviewsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading reviews:', error);
        setError('เกิดข้อผิดพลาดในการโหลดรีวิว');
        setLoading(false);
      }
    );

    return unsubscribe;
  };

  // Load reviews for current user
  useEffect(() => {
    if (!currentUser) {
      setReviews([]);
      return;
    }

    const unsubscribe = loadUserReviews();
    return () => unsubscribe();
  }, [currentUser]);

  // Load reviews for a specific user (for profile display)
  const loadUserProfileReviews = async (userId, userRole) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading profile reviews for userId:', userId, 'userRole:', userRole);
      const reviewsRef = collection(db, reviewModel.collection);
      
      // Query without orderBy to avoid index requirement
      const q = query(
        reviewsRef,
        where('revieweeId', '==', userId),
        where('status', '==', reviewModel.status.APPROVED)
      );

      const snapshot = await getDocs(q);
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort manually after fetching
      reviewsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order (newest first)
      });

      return reviewsData;
    } catch (error) {
      console.error('Error loading profile reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load reviews for a specific booking
  const loadBookingReviews = async (bookingId) => {
    try {
      setLoading(true);
      setError(null);

      const reviewsRef = collection(db, reviewModel.collection);
      const q = query(
        reviewsRef,
        where('bookingId', '==', bookingId)
      );

      const snapshot = await getDocs(q);
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort manually after fetching
      reviewsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order (newest first)
      });

      return reviewsData;
    } catch (error) {
      console.error('Error loading booking reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Create a new review
  const createReview = async (reviewData) => {
    if (!currentUser) {
      throw new Error('กรุณาล็อกอินก่อน');
    }

    try {
      setLoading(true);
      setError(null);

      // Validate review data
      const validatedData = validateReviewData(reviewData);
      
      const newReview = {
        ...reviewModel.defaultDocument,
        ...validatedData,
        reviewerId: currentUser.uid,
        reviewerName: currentUser.displayName || currentUser.email,
        reviewerRole: currentUser.role || 'customer',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: reviewModel.status.APPROVED
      };

      console.log('ReviewContext: Creating review with data:', newReview);
      console.log('ReviewContext: Collection name:', reviewModel.collection);
      
      const docRef = await addDoc(collection(db, reviewModel.collection), newReview);
      
      // ส่งการแจ้งเตือนให้ผู้ขาย/ร้านค้า
      try {
        if (newReview.type === reviewModel.types.PRODUCT && newReview.productId) {
          // ดึงข้อมูลสินค้าเพื่อหา storeId
          const productRef = doc(db, 'products', newReview.productId);
          const productSnap = await getDoc(productRef);
          if (productSnap.exists()) {
            const productData = productSnap.data();
            if (productData.storeId) {
              const storeRef = doc(db, 'stores', productData.storeId);
              const storeSnap = await getDoc(storeRef);
              if (storeSnap.exists()) {
                const storeData = storeSnap.data();
                await createNotification(
                  storeData.ownerId,
                  notificationModel.types.REVIEW_NEW,
                  'มีรีวิวสินค้าใหม่',
                  `${newReview.reviewerName} ให้รีวิวสินค้า "${productData.name}"`,
                  { productId: newReview.productId, reviewId: docRef.id, storeId: productData.storeId }
                );
              }
            }
          }
        } else if (newReview.type === reviewModel.types.STORE && newReview.storeId) {
          // ดึงข้อมูลร้านค้า
          const storeRef = doc(db, 'stores', newReview.storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            const storeData = storeSnap.data();
            await createNotification(
              storeData.ownerId,
              notificationModel.types.REVIEW_NEW,
              'มีรีวิวร้านค้าใหม่',
              `${newReview.reviewerName} ให้รีวิวร้านค้า "${storeData.name}"`,
              { storeId: newReview.storeId, reviewId: docRef.id }
            );
          }
        }
      } catch (notifError) {
        console.error('Error creating review notification:', notifError);
      }
      
      console.log('ReviewContext: Document reference:', docRef);
      console.log('ReviewContext: Document ID:', docRef.id);
      console.log('ReviewContext: Document path:', docRef.path);
      console.log('Review created successfully:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating review:', error);
      setError('เกิดข้อผิดพลาดในการสร้างรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing review
  const updateReview = async (reviewId, updateData) => {
    if (!currentUser) {
      throw new Error('กรุณาล็อกอินก่อน');
    }

    try {
      setLoading(true);
      setError(null);

      const reviewRef = doc(db, reviewModel.collection, reviewId);
      const updatePayload = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(reviewRef, updatePayload);
      
      console.log('Review updated successfully:', reviewId);
      return true;
    } catch (error) {
      console.error('Error updating review:', error);
      setError('เกิดข้อผิดพลาดในการอัปเดตรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Respond to a review
  const respondToReview = async (reviewId, responseText) => {
    if (!currentUser) {
      throw new Error('กรุณาล็อกอินก่อน');
    }

    console.log('ReviewContext: Review ID received:', reviewId);
    console.log('ReviewContext: Review ID type:', typeof reviewId);
    
    if (!reviewId || reviewId.trim() === '') {
      throw new Error('Review ID is required and cannot be empty');
    }

    console.log('ReviewContext: Responding to review ID:', reviewId, 'with text:', responseText);
    try {
      setLoading(true);
      setError(null);

      const reviewRef = doc(db, reviewModel.collection, reviewId);
      console.log('ReviewContext: Document reference:', reviewRef.path);
      const response = {
        comment: responseText,
        respondedAt: new Date().toISOString(),
        respondedBy: currentUser.uid
      };

      await updateDoc(reviewRef, {
        response,
        updatedAt: new Date().toISOString()
      });
      
      console.log('Response added successfully:', reviewId);
      return true;
    } catch (error) {
      console.error('Error responding to review:', error);
      setError('เกิดข้อผิดพลาดในการตอบรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a review
  const deleteReview = async (reviewId) => {
    if (!currentUser) {
      throw new Error('กรุณาล็อกอินก่อน');
    }

    try {
      setLoading(true);
      setError(null);

      await deleteDoc(doc(db, reviewModel.collection, reviewId));
      
      console.log('Review deleted successfully:', reviewId);
      return true;
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('เกิดข้อผิดพลาดในการลบรีวิว');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Validate review data
  const validateReviewData = (data) => {
    const validation = reviewModel.validation;
    
    // Validate rating
    if (!data.rating || data.rating < validation.rating.min || data.rating > validation.rating.max) {
      throw new Error('คะแนนรีวิวต้องอยู่ระหว่าง 1-5 ดาว');
    }

    // Validate title
    if (!data.title || data.title.length < validation.title.minLength || data.title.length > validation.title.maxLength) {
      throw new Error('หัวข้อรีวิวต้องมีความยาว 5-100 ตัวอักษร');
    }

    // Validate comment
    if (!data.comment || data.comment.length < validation.comment.minLength || data.comment.length > validation.comment.maxLength) {
      throw new Error('ความคิดเห็นต้องมีความยาว 5-1000 ตัวอักษร');
    }

    // Validate tags
    if (data.tags && data.tags.length > validation.tags.maxItems) {
      throw new Error('แท็กต้องไม่เกิน 5 ตัว');
    }

    return data;
  };

  // Check if user can review a booking
  const canReviewBooking = (booking) => {
    if (!currentUser || !booking) return false;
    
    // Check if booking is completed or confirmed
    if (booking.status !== 'completed' && booking.status !== 'confirmed') return false;
    
    // Check if user is involved in the booking
    if (currentUser.uid !== booking.userId && currentUser.uid !== booking.girlId) {
      return false;
    }
    
    // Check if review already exists
    const existingReview = reviews.find(review => 
      review.bookingId === booking.id && review.reviewerId === currentUser.uid
    );
    
    return !existingReview;
  };

  // Load reviews for a specific product
  const loadProductReviews = async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const reviewsRef = collection(db, reviewModel.collection);
      const q = query(
        reviewsRef,
        where('productId', '==', productId),
        where('type', '==', reviewModel.types.PRODUCT),
        where('status', '==', reviewModel.status.APPROVED)
      );

      const snapshot = await getDocs(q);
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort manually after fetching
      reviewsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order (newest first)
      });

      return reviewsData;
    } catch (error) {
      console.error('Error loading product reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิวสินค้า');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load reviews for a specific store
  const loadStoreReviews = async (storeId) => {
    try {
      setLoading(true);
      setError(null);

      const reviewsRef = collection(db, reviewModel.collection);
      const q = query(
        reviewsRef,
        where('storeId', '==', storeId),
        where('type', '==', reviewModel.types.STORE),
        where('status', '==', reviewModel.status.APPROVED)
      );

      const snapshot = await getDocs(q);
      const reviewsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort manually after fetching
      reviewsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Descending order (newest first)
      });

      return reviewsData;
    } catch (error) {
      console.error('Error loading store reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิวร้านค้า');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get review statistics for a user
  const getUserReviewStats = (userId) => {
    const userReviews = reviews.filter(review => review.revieweeId === userId);
    
    if (userReviews.length === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        commonTags: []
      };
    }

    return {
      averageRating: reviewModel.statistics.calculateAverageRating(userReviews),
      totalReviews: userReviews.length,
      ratingDistribution: reviewModel.statistics.calculateRatingDistribution(userReviews),
      commonTags: reviewModel.statistics.getMostCommonTags(userReviews)
    };
  };

  // Get review statistics for a product
  const getProductReviewStats = async (productId) => {
    try {
      const productReviews = await loadProductReviews(productId);
      
      if (productReviews.length === 0) {
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          commonTags: []
        };
      }

      return {
        averageRating: reviewModel.statistics.calculateAverageRating(productReviews),
        totalReviews: productReviews.length,
        ratingDistribution: reviewModel.statistics.calculateRatingDistribution(productReviews),
        commonTags: reviewModel.statistics.getMostCommonTags(productReviews)
      };
    } catch (error) {
      console.error('Error getting product review stats:', error);
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        commonTags: []
      };
    }
  };

  // Get review statistics for a store
  const getStoreReviewStats = async (storeId) => {
    try {
      const storeReviews = await loadStoreReviews(storeId);
      
      if (storeReviews.length === 0) {
        return {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          commonTags: []
        };
      }

      return {
        averageRating: reviewModel.statistics.calculateAverageRating(storeReviews),
        totalReviews: storeReviews.length,
        ratingDistribution: reviewModel.statistics.calculateRatingDistribution(storeReviews),
        commonTags: reviewModel.statistics.getMostCommonTags(storeReviews)
      };
    } catch (error) {
      console.error('Error getting store review stats:', error);
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        commonTags: []
      };
    }
  };

  // Check if user can review a product
  const canReviewProduct = async (productId, orderId) => {
    if (!currentUser) return false;
    
    // Check if user has a delivered order with this product
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('buyerId', '==', currentUser.uid),
        where('status', '==', 'delivered')
      );
      
      const snapshot = await getDocs(q);
      const hasDeliveredOrder = snapshot.docs.some(doc => {
        const order = doc.data();
        return order.id === orderId && order.items?.some(item => item.productId === productId);
      });
      
      if (!hasDeliveredOrder) return false;
      
      // Check if review already exists
      const existingReview = await loadProductReviews(productId);
      const hasExistingReview = existingReview.some(review => 
        review.reviewerId === currentUser.uid && review.orderId === orderId
      );
      
      return !hasExistingReview;
    } catch (error) {
      console.error('Error checking can review product:', error);
      return false;
    }
  };

  // Check if user can review a store
  const canReviewStore = async (storeId, orderId) => {
    if (!currentUser) return false;
    
    // Check if user has a delivered order from this store
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('buyerId', '==', currentUser.uid),
        where('storeId', '==', storeId),
        where('status', '==', 'delivered')
      );
      
      const snapshot = await getDocs(q);
      const hasDeliveredOrder = snapshot.docs.some(doc => doc.id === orderId);
      
      if (!hasDeliveredOrder) return false;
      
      // Check if review already exists
      const existingReview = await loadStoreReviews(storeId);
      const hasExistingReview = existingReview.some(review => 
        review.reviewerId === currentUser.uid && review.orderId === orderId
      );
      
      return !hasExistingReview;
    } catch (error) {
      console.error('Error checking can review store:', error);
      return false;
    }
  };

  const value = {
    reviews,
    loading,
    error,
    createReview,
    updateReview,
    respondToReview,
    deleteReview,
    loadUserProfileReviews,
    loadBookingReviews,
    loadProductReviews,
    loadStoreReviews,
    canReviewBooking,
    canReviewProduct,
    canReviewStore,
    getUserReviewStats,
    getProductReviewStats,
    getStoreReviewStats,
    reviewUtils
  };

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
};
